import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Button, InputTextFormik} from '../../../../../../_gori/partials/widgets'
import {useIntl} from 'react-intl'
import {KTSVG} from '../../../../../../_gori/helpers'
import UseYupValidate from '../../../../../../_gori/hooks/UseYupValidate'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {debounce} from 'lodash'
import A1BatchService from '../../../core/_requests'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'

const MAX_RESULTS_COUNT = 30
const MIN_NUMBER_TO_SEARCH = 5

const addOutsideClickListener = (ref, onOutsideClick) => {
  const handleWindowClick = (e) => {
    if (onOutsideClick) {
      let node = e.target
      while (node) {
        if (ref.current === node) {
          return
        }
        node = node.parentNode
      }
      onOutsideClick()
    }
  }
  window.addEventListener('click', handleWindowClick)
  return (): void => {
    window.removeEventListener('click', handleWindowClick)
  }
}

const useOutsideClickHandler = (onOutsideClick) => {
  const ref = useRef(null)
  useEffect(() => addOutsideClickListener(ref, onOutsideClick), [onOutsideClick])
  return ref
}

type BatchManualInputProps = {
  getBatchInfo: (manualCode: string) => void
  handleScan: (e: KeyboardEvent) => void
}
export const BatchManualInput = ({getBatchInfo, handleScan}: BatchManualInputProps) => {
  const {newCancelToken, cancelToken} = useCancelToken()

  const [showManualInput, setShowManualInput] = useState<boolean>(false)
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false)
  const [searchResults, setSearchResults] = useState<
    {
      label: string
      value: string
    }[]
  >([])

  const outsideRef = useOutsideClickHandler(() => setShowSearchResults(false))

  const intl = useIntl()

  const {stringYup} = UseYupValidate()

  const validateSchema = Yup.object({
    manualCode: stringYup(255, 'BARCODE', true),
  })

  const formik = useFormik({
    initialValues: {
      manualCode: '',
    },
    enableReinitialize: true,
    validationSchema: validateSchema,
    onSubmit: async (values) => {
      getBatchInfo(values.manualCode.trim())
      formik.setFieldValue('manualCode', '')
      formik.setFieldTouched('manualCode', false)
      cancelToken()
      setShowSearchResults(false)
      setSearchResults([])
    },
  })

  const onCloseManualInput = () => {
    setShowManualInput(false)
    formik.setFieldValue('manualCode', '')
    formik.setFieldTouched('manualCode', false)
  }

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName.toLowerCase()
      const isTyping = ['input'].includes(tag)
      if (isTyping) {
        if (showManualInput && e.key === 'Enter') {
          if (!formik.values?.manualCode.trim()) return

          getBatchInfo(formik.values?.manualCode.trim())
          return
        }
        return
      }
      handleScan(e)
    },
    [formik.values?.manualCode, getBatchInfo, handleScan, showManualInput]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  const mapOrdersToOptions = (orders) => {
    const result: {label: string; value: string}[] = []
    let count = 0

    for (const order of orders) {
      const trackingNumbers = order.latest_shipment?.tracking_numbers ?? []

      for (const tn of trackingNumbers) {
        if (count >= MAX_RESULTS_COUNT) return result

        result.push({
          label: order.code,
          value: tn,
        })

        count++
      }
    }

    return result
  }

  const debounceSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        const results: any = await A1BatchService.searchTrackingNumber(value, {
          cancelToken: newCancelToken(),
        })

        const mappingResults = mapOrdersToOptions(results?.orders || [])
        if (!mappingResults.length) {
          setSearchResults([])
          setShowSearchResults(false)
          return
        }
        setSearchResults(mappingResults)
        setShowSearchResults(true)
      }, 300),
    [newCancelToken]
  )

  const handleSearch = useCallback(
    (value: string) => {
      if (!value || value.length < MIN_NUMBER_TO_SEARCH) {
        setSearchResults([])
        setShowSearchResults(false)

        return
      }

      debounceSearch(value)
    },
    [debounceSearch]
  )

  useEffect(() => {
    return () => {
      debounceSearch.cancel()
    }
  }, [debounceSearch])

  const onSelectResultItem = (item) => {
    formik.setFieldValue('manualCode', item.value)
    setShowSearchResults(false)
  }

  return (
    <div className='mt-6'>
      {showManualInput ? (
        <div className='d-flex justify-between align-items-center gap-5'>
          <div className='border border-primary rounded-3 p-5 d-flex flex-fill align-items-center gap-3'>
            <KTSVG path='/media/gori/a1/barcode.svg' className='svg-icon-1' />
            <div className='d-flex flex-column flex-fill' ref={outsideRef}>
              <InputTextFormik
                className='w-100'
                inputClassName={'border-0 h-30px'}
                required
                formik={formik}
                name='manualCode'
                placeholder={intl.formatMessage({id: 'MANUAL_INPUT_PLACEHOLDER'})}
                onChange={(e) => handleSearch(e?.target?.value?.trim())}
                onFocus={() => handleSearch(formik.values?.manualCode)}
              />
              <div className='input-group'>
                <div data-bs-toggle='dropdown' aria-expanded={showSearchResults}></div>
                <div
                  className={`dropdown-menu fw-bold fs-7 w-100 overflow-y-auto ${
                    showSearchResults ? 'show' : ''
                  }`}
                  style={{maxHeight: 300, background: 'white'}}
                >
                  {searchResults.map((item: any, index: number) => {
                    return (
                      <div
                        className={`map-item py-4 text-gray-600 text-hover-dark cursor-pointer fw-bold border-bottom border-1 border-gray-200`}
                        key={index}
                        onClick={() => onSelectResultItem(item)}
                      >
                        <span className='menu-link fs-6'>
                          {item.value} ({item.label})
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
            <Button
              label={intl.formatMessage({id: 'SUBMIT'})}
              event={formik.handleSubmit}
              className={clsx('py-0 px-1 btn fs-inherit btn-advanced-tertiary fw-bolder', {
                'cursor-not-allowed text-gray-500':
                  formik.isSubmitting || !formik.values.manualCode.trim(),
              })}
              loading={formik.isSubmitting}
              disabled={formik.isSubmitting || !formik.values.manualCode.trim()}
            />
          </div>
          <div onClick={onCloseManualInput} className='cursor-pointer text-hover-primary'>
            <KTSVG path='/media/gori/a1/close.svg' className='svg-icon-1' />
          </div>
        </div>
      ) : (
        <div className='text-primary-deep fw-bolder mt-6'>
          {intl.formatMessage({id: 'HAVING_TROUBLE_SCANNING'})}{' '}
          <span
            className='text-decoration-underline text-hover-primary cursor-pointer'
            onClick={() => setShowManualInput(true)}
          >
            {intl.formatMessage({id: 'MANUAL_INPUT'})}
          </span>
        </div>
      )}
    </div>
  )
}
